/*
  There are a lot of similar top-top-level components, which
    just wrappers above more specific childs with unified general
    functionality related to the visitor page.
  Please dont combine them into one super-components, I think
    in this style they are more flexible and maintainable.
 */

import * as React from 'react';

import { VisitorPageSection } from '../VisitorPageSection';
import { ChallengeShare } from '../../../../components/ChallengeShare';
import { ChallengeAgenda } from '../../../../components/ChallengeAgenda';
import { SocialGroup } from '../../../../../../../components-shared/SocialGroup';
import { VisitorPageProgramDetails } from './VisitorPageProgramDetails';
import { Pricing } from '../../../../components/Pricing/Pricing';
import { JoinButtonIds, VisitorPageJoinButton } from './VisitorPageJoinButton';

import { useTranslation } from '@wix/yoshi-flow-editor';
import { useSettings } from '@wix/tpa-settings/react';
import { useSocialGroups } from '../../../../../../../contexts/SocialGroups/SocialGroupsContext';
import { useUser } from '../../../../../../../contexts/User/UserContext';
import { useVisitorPageBase } from '../../contexts/VisitorPageBase/VisitorPageBaseContext';

import { SettingsKeysAbbreviation } from '../../../../../Settings/constants/settingsKeysAbbreviation';
import settingsParams from '../../../../../settingsParams';
import { isPricingPickingForbidden } from '../../../../../../../selectors/challenges';
import {
  FCWithChildren,
  getChallengePaidPlans,
  isChallengeFree,
} from '@wix/challenges-web-library';
import { isUserJoinedAlready } from '../../../../../../../contexts/User/helpers/userTypeHandlers';

import { sectionIds } from '../../../../../DeprecatedSettings/tabs/Sections/components/VisitorV2SectionsTabContent/constants/sectionIds';
import { TextAlignment } from '../../../../../Settings/challengeSettings/challengeSettings.types';
import { VisitorPageInstructors } from './VisitorPageInstructors';
import { useSidebarItems } from '../../../../../DeprecatedSettings/tabs/Sections/components/VisitorV2SectionsTabContent/constants/splitItemsHelper';
import { st, classes } from './VisitorPageSections.st.css';
import { useCSSPBEnabled } from '../../../../../../../hooks/useCSSPBStyle';
import { useMemberPaidPlans } from '../../../../../../../contexts/storage-contexts/PaidPlans';
import { useChallengeData } from '../../../../../../../contexts/storage-contexts/Challenge';
import { isSecretProgram } from '../../../../../../../selectors/isSecretProgram';

export const VisitorPageShare: FCWithChildren<{
  className?: string;
  alignment?: TextAlignment;
}> = ({ className = null, alignment = TextAlignment.Left }) => {
  const {
    challengeData: { challenge },
  } = useChallengeData();
  const settings = useSettings();
  const cssPBEnabled = useCSSPBEnabled();
  const showSocial =
    cssPBEnabled || settings.get(settingsParams.socialSharingIsEnabled);
  const isVisible = showSocial && !isSecretProgram(challenge);
  const showTitle =
    cssPBEnabled ||
    settings.get(
      settingsParams[SettingsKeysAbbreviation.visitorPageToggleShareTitle],
    );
  const title = showTitle
    ? settings.get(
        settingsParams[SettingsKeysAbbreviation.visitorPageShareTitle],
      )
    : null;

  return isVisible ? (
    <VisitorPageSection
      className={st(
        className,
        cssPBEnabled ? classes.opgSocialSharingIsEnabled : undefined,
      )}
      title={title}
      alignment={alignment}
    >
      <ChallengeShare alignment={alignment} />
    </VisitorPageSection>
  ) : null;
};

export const VisitorPageAgenda: FCWithChildren<{
  className?: string;
  alignment?: TextAlignment;
}> = ({ className = null, alignment = TextAlignment.Left }) => {
  const settings = useSettings();
  const cssPBEnabled = useCSSPBEnabled();
  const isVisible =
    cssPBEnabled || settings.get(settingsParams.agendaIsEnabled);
  const title = isVisible ? settings.get(settingsParams.agendaTitleText) : null;

  return isVisible ? (
    <VisitorPageSection
      title={title}
      className={st(
        className,
        cssPBEnabled ? classes.opgAgendaIsEnabled : undefined,
      )}
      alignment={alignment}
    >
      <ChallengeAgenda alignment={alignment} />
    </VisitorPageSection>
  ) : null;
};

export const VisitorPageGroup: FCWithChildren<{
  className?: string;
  boxElementClassName?: string;
  alignment?: TextAlignment;
}> = ({
  className = null,
  boxElementClassName = null,
  alignment = TextAlignment.Left,
}) => {
  const { t } = useTranslation();
  const { socialGroup } = useSocialGroups();
  const settings = useSettings();
  const cssPBEnabled = useCSSPBEnabled();
  if (!socialGroup) {
    return null;
  }
  const showTitle =
    cssPBEnabled ||
    settings.get(
      settingsParams[
        SettingsKeysAbbreviation.visitorPageToggleGroupDiscussionTitle
      ],
    );
  const title = showTitle
    ? settings.get(
        settingsParams[
          SettingsKeysAbbreviation.visitorPageGroupDiscussionTitle
        ],
      )
    : null;

  return (
    <VisitorPageSection
      title={title}
      className={st(
        className,
        cssPBEnabled && classes.opgVisitorPageGroupDiscussion,
      )}
      alignment={alignment}
    >
      <SocialGroup
        className={boxElementClassName}
        subTitle={t('live-site.groups.section-subtitle.not-joined')}
        showButton={false}
        alignment={alignment}
      />
    </VisitorPageSection>
  );
};

export const VisitorPageDetails: FCWithChildren<{
  className?: string;
  alignment?: TextAlignment;
  isUserRequestedToJoin?: boolean;
}> = ({
  className = null,
  alignment = TextAlignment.Left,
  isUserRequestedToJoin = false,
}) => {
  const settings = useSettings();
  const cssPBEnabled = useCSSPBEnabled();

  const showTitle =
    cssPBEnabled ||
    settings.get(
      settingsParams[SettingsKeysAbbreviation.visitorPageToggleAboutTitle],
    );
  const title = showTitle
    ? settings.get(
        settingsParams[SettingsKeysAbbreviation.visitorPageAboutTitle],
      )
    : null;

  const showJoinBtn =
    !isUserRequestedToJoin &&
    (cssPBEnabled ||
      settings.get(
        settingsParams[
          SettingsKeysAbbreviation.visitorPageToggleAboutJoinButtonTitle
        ],
      ));
  return (
    <VisitorPageSection
      title={title}
      className={st(className, cssPBEnabled && classes.opgVisitorPageAbout)}
      alignment={alignment}
    >
      <VisitorPageProgramDetails alignment={alignment} />

      {showJoinBtn ? (
        <VisitorPageJoinButtonBox
          className={st(
            className,
            cssPBEnabled
              ? classes.opgVisitorPageToggleAboutJoinButtonTitle
              : undefined,
          )}
          alignment={alignment}
          title={settings.get(
            settingsParams[
              SettingsKeysAbbreviation.visitorPageAboutJoinButtonTitle
            ],
          )}
        />
      ) : null}
    </VisitorPageSection>
  );
};

export const VisitorPagePricing: FCWithChildren<{
  className?: string;
  boxElementClassName?: string;
  alignment?: TextAlignment;
  isUserRequestedToJoin?: boolean;
}> = ({
  className = null,
  boxElementClassName = null,
  alignment = TextAlignment.Left,
  isUserRequestedToJoin = false,
}) => {
  const {
    challengeData: { challenge },
  } = useChallengeData();
  const { userType } = useUser();
  const { userPaidPlans } = useMemberPaidPlans();
  const settings = useSettings();
  const cssPBEnabled = useCSSPBEnabled();
  const showTitle =
    cssPBEnabled ||
    settings.get(
      settingsParams[SettingsKeysAbbreviation.visitorPageTogglePriceTitle],
    );
  const title = showTitle
    ? settings.get(
        settingsParams[SettingsKeysAbbreviation.visitorPagePriceTitle],
      )
    : null;
  const {
    challengeJoinRestrictions,
    selectedPaymentOption,
    isDisableEligiblePlans,
    onPaymentSelected,
  } = useVisitorPageBase();

  const challengePaidPlans = getChallengePaidPlans(
    challenge?.id,
    userPaidPlans,
  );
  const isFree = isChallengeFree(
    challenge?.settings?.pricing as any,
    challengePaidPlans.length,
  );
  const showPrice =
    cssPBEnabled || settings.get(settingsParams.displayPriceForFreePrograms);
  const isVisible = (!isFree || showPrice) && !isUserJoinedAlready(userType);
  const isDisabled =
    !!challengeJoinRestrictions?.length ||
    isPricingPickingForbidden(challenge as any, userType, userPaidPlans);

  const showBtn =
    !isUserRequestedToJoin &&
    (cssPBEnabled ||
      settings.get(
        settingsParams[
          SettingsKeysAbbreviation.visitorPageTogglePriceJoinButtonTitle
        ],
      ));
  return isVisible ? (
    <VisitorPageSection
      title={title}
      className={st(
        className,
        cssPBEnabled && classes.opgVisitorPagePrice,
        cssPBEnabled && isFree
          ? classes.opgDisplayPriceForFreePrograms
          : undefined,
      )}
      alignment={alignment}
    >
      <Pricing
        className={boxElementClassName}
        alignment={alignment}
        disabled={isDisabled}
        disabledEligible={isDisableEligiblePlans}
        selectedPaymentOption={selectedPaymentOption}
        onPaymentSelected={onPaymentSelected as any}
      />

      {showBtn ? (
        <VisitorPageJoinButtonBox
          className={st(
            className,
            cssPBEnabled && classes.opgVisitorPageTogglePriceJoinButtonTitle,
          )}
          alignment={alignment}
          title={settings.get(
            settingsParams[
              SettingsKeysAbbreviation.visitorPagePriceJoinButtonTitle
            ],
          )}
        />
      ) : null}
    </VisitorPageSection>
  ) : null;
};

export const VisitorPageInstructorsBox: FCWithChildren<{
  className?: string;
  alignment?: TextAlignment;
}> = ({ className = null, alignment = TextAlignment.Left }) => {
  const settings = useSettings();
  const {
    requestInstructorsList,
    challengeData: { instructorsData, challenge },
  } = useChallengeData();
  const cssPBEnabled = useCSSPBEnabled();
  const showOwner = cssPBEnabled || settings.get(settingsParams.displayOwner);
  const isVisible = showOwner && instructorsData?.instructors?.length;
  const showTitle =
    cssPBEnabled ||
    settings.get(
      settingsParams[SettingsKeysAbbreviation.visitorPageToggleInstructorTitle],
    );
  const title = showTitle
    ? settings.get(
        settingsParams[SettingsKeysAbbreviation.visitorPageInstructorTitle],
      )
    : null;

  const sidebarItems = useSidebarItems();
  const sidebarItemsIds = sidebarItems.map((item) => item.id);
  const isInsideSidebar = sidebarItemsIds.includes('instructor');

  React.useEffect(() => {
    if (!instructorsData) {
      void requestInstructorsList?.(challenge?.id);
    }
  }, [challenge?.id, instructorsData, requestInstructorsList]);

  return isVisible ? (
    <VisitorPageSection
      title={title}
      className={st(className, cssPBEnabled && classes.opgInstructorBox)}
      alignment={alignment}
    >
      <VisitorPageInstructors
        alignment={alignment}
        layout={isInsideSidebar ? 'column' : 'box'}
      />
    </VisitorPageSection>
  ) : null;
};

export const VisitorPageJoinButtonBox: FCWithChildren<
  {
    buttonId?: JoinButtonIds;
    className?: string;
    alignment?: TextAlignment;
    title?: string;
  } & React.HTMLAttributes<HTMLElement>
> = ({
  className = null,
  buttonId = JoinButtonIds.InsideSection,
  alignment = TextAlignment.Left,
  title,
  style,
}) => {
  const { challengeJoinRestrictions } = useVisitorPageBase();
  const isInsidePageSection = buttonId === JoinButtonIds.Bottom;
  const isDisabled = !!challengeJoinRestrictions?.length;

  const btn = (
    <VisitorPageJoinButton
      buttonId={buttonId}
      title={title}
      alignment={alignment}
      style={style}
      isDisabled={isDisabled}
      className={isInsidePageSection ? undefined : className}
    />
  );

  return isInsidePageSection ? (
    <VisitorPageSection
      className={className}
      alignment={alignment}
      style={style}
    >
      {btn}
    </VisitorPageSection>
  ) : (
    <>{btn}</>
  );
};

export const VisitorPageSectionsMapper: FCWithChildren<{
  sections: typeof sectionIds;
  sectionClassName?: string;
  boxElementClassName?: string;
  alignment?: TextAlignment;
  isUserRequestedToJoin?: boolean;
}> = ({
  sections,
  sectionClassName = null,
  boxElementClassName,
  alignment,
  isUserRequestedToJoin = false,
}) => {
  return (
    <>
      {sections.map((s, i) => {
        switch (s) {
          case 'about':
            return (
              <VisitorPageDetails
                key={i}
                className={sectionClassName}
                alignment={alignment}
                isUserRequestedToJoin={isUserRequestedToJoin}
              />
            );
          case 'overview':
            return (
              <VisitorPageAgenda
                key={i}
                className={sectionClassName}
                alignment={alignment}
              />
            );
          case 'instructor':
            return (
              <VisitorPageInstructorsBox
                key={i}
                className={sectionClassName}
                alignment={alignment}
              />
            );
          case 'price':
            return (
              <VisitorPagePricing
                key={i}
                className={sectionClassName}
                boxElementClassName={boxElementClassName}
                alignment={alignment}
                isUserRequestedToJoin={isUserRequestedToJoin}
              />
            );
          case 'group_discussion':
            return (
              <VisitorPageGroup
                key={i}
                className={sectionClassName}
                boxElementClassName={boxElementClassName}
                alignment={alignment}
              />
            );
          case 'share':
            return (
              <VisitorPageShare
                key={i}
                className={sectionClassName}
                alignment={alignment}
              />
            );
        }
      })}
    </>
  );
};
